/**
 * Fixed-point helpers for the prototype's fixture maths. Quantities and money stay decimal
 * strings (AGENTS.md rule 4), so arithmetic runs on scaled bigints rather than JS numbers.
 */
const SCALE = 3
const FACTOR = 10n ** BigInt(SCALE)

export const toUnits = (value: string): bigint => {
  const negative = value.startsWith('-')
  const [whole = '0', fraction = ''] = (negative ? value.slice(1) : value).split('.')
  const units = BigInt(whole || '0') * FACTOR + BigInt(fraction.padEnd(SCALE, '0').slice(0, SCALE))
  return negative ? -units : units
}

export const fromUnits = (units: bigint, scale = SCALE): string => {
  const negative = units < 0n
  const absolute = negative ? -units : units
  const whole = absolute / FACTOR
  const fraction = String(absolute % FACTOR)
    .padStart(SCALE, '0')
    .slice(0, scale)
  const body = scale > 0 ? `${whole}.${fraction}` : String(whole)
  return negative ? `-${body}` : body
}

export const add = (...values: string[]): string =>
  fromUnits(values.reduce((sum, value) => sum + toUnits(value), 0n))

export const sub = (left: string, right: string): string =>
  fromUnits(toUnits(left) - toUnits(right))

export const mul = (left: string, right: string): string =>
  fromUnits((toUnits(left) * toUnits(right)) / FACTOR)

export const min = (left: string, right: string): string =>
  toUnits(left) <= toUnits(right) ? left : right

export const max = (left: string, right: string): string =>
  toUnits(left) >= toUnits(right) ? left : right

export const isPositive = (value: string): boolean => toUnits(value) > 0n

export const compare = (left: string, right: string): number => {
  const difference = toUnits(left) - toUnits(right)
  return difference === 0n ? 0 : difference > 0n ? 1 : -1
}

export const toMoney = (value: string): string => fromUnits(toUnits(value), 2)

/** Whole-number percentage of `part` in `total`, for progress bars only. */
export const percent = (part: string, total: string): number => {
  const denominator = toUnits(total)
  if (denominator <= 0n) return 0
  const value = Number((toUnits(part) * 100n) / denominator)
  return Math.max(0, Math.min(100, value))
}

/**
 * VAT contained in a price that already includes it: gross × rate / (100 + rate), rounded half
 * up to kopecks. Display only; the real figure comes from the API (spec §10.1).
 */
export const vatIncluded = (gross: string, ratePercent: string): string => {
  const denominator = 100n * FACTOR + toUnits(ratePercent)
  const units = (toUnits(gross) * toUnits(ratePercent) * 2n + denominator) / (2n * denominator)
  const cents = (units + 5n) / 10n
  return fromUnits(cents * 10n, 2)
}

/** left ÷ right at the working scale, rounded half up. Display and average cost only. */
export const div = (left: string, right: string): string => {
  const denominator = toUnits(right)
  if (denominator === 0n) return '0'
  const scaled = toUnits(left) * FACTOR * 2n
  const quotient = scaled / denominator
  return fromUnits((quotient + (quotient >= 0n ? 1n : -1n)) / 2n)
}
